@if (
  {
    user: user$ | async,
    sked: sked$ | async,
    task: task$ | async,
    projectsMap: projectsMap$ | async,
    locationsMap: locationsMap$ | async,
    skedTimeStatus: skedTimeStatus$ | async,
    loading: loading$ | async,
    imageLoading: imageLoading$ | async,
    uploadProgress: uploadProgress$ | async,
    reversedNotes: reversedNotes$ | async,
    usersMap$: usersMap$ | async,
    activeSubtask: activeSubtask$ | async,
    activeSubtaskIndex: activeSubtaskIndex$ | async,
    mobileSubtaskDetailView: mobileSubtaskDetailView$ | async,
    taskHasOneSubtask: taskHasOneSubtask$ | async,
    areAllMetricsReadOnly: areAllMetricsReadOnly$ | async,
    availableTaskActions: availableTaskActions$ | async,
    ableToCompleteSubtaskIndex: ableToCompleteSubtaskIndex$ | async,
    taskExpired: taskExpired$ | async
  };
  as state
) {
  @if (state.loading.task) {
    <div class="oph-loading-container">
      <oph-loading></oph-loading>
    </div>
  } @else {
    <oph-back-button (buttonClick)="onBack()" class="desktop-back-button"></oph-back-button>
    <!-- ****************************************************************************** -->
    <!-- ****************************** DESKTOP VIEW ********************************** -->
    <!-- ****************************************************************************** -->
    <div class="desktop">
      <div class="desktop-left-container">
        <current-sked-task-detail-info
          [task]="state.task"
          [project]="state.projectsMap?.[state.task?.projectId]"
          [coinTypeName]="(coinTypesMap$ | async)[state.task.taskCoinType]?.name"
          [isMobile]="false"
          [showProjectAndCoin]="true"
        >
          @if (notInSked) {
            <current-sked-task-detail-status-button
              [loading]="state.loading"
              [task]="state.task"
              (statusAction)="onUpdateTaskStatus($event)"
              [availableTaskActions]="state.availableTaskActions"
            ></current-sked-task-detail-status-button>
          }
        </current-sked-task-detail-info>

        @for (subtask of state.task.subtasks; track subtask; let subtaskIndex = $index) {
          <button
            class="subtask"
            type="button"
            (click)="onSubtaskClick(subtask, subtaskIndex)"
            [class.active-subtask]="state.activeSubtask?._id === subtask._id"
          >
            <current-sked-checkbox
              [checked]="subtask.complete"
              [loading]="state.loading?.subtaskIndex === subtaskIndex && state.loading?.checkbox"
              [disabled]="!subtask.complete"
              (checkboxClick)="onUpdateSubtaskStatus($event, subtask, subtaskIndex, false)"
            ></current-sked-checkbox>
            <span class="subtask-title">{{ subtask.title }}</span>

            @if (state.activeSubtask?._id === subtask._id) {
              <div class="active-subtask-indicator"></div>
              <img
                src="assets/img/icons/active-subtask-top.svg"
                alt="active-subtask-indicator"
                class="active-subtask-indicator-top"
              />
              <img
                src="assets/img/icons/active-subtask-bottom.svg"
                alt="active-subtask-indicator"
                class="active-subtask-indicator-bottom"
              />
            }
          </button>
        }

        @if (state.task?.status !== TaskStatuses.Completed) {
          <button
            type="button"
            *userHasLens="LensSettings.CompleteTasksWithoutSubtaskCompletion"
            class="complete-task-button"
            (click)="onUpdateTaskStatus(TaskStatusActions.Complete)"
          >
            @if (state.loading.action) {
              <div class="oph-green-spinner">
                <mat-spinner class="custom-color" diameter="20"></mat-spinner>
              </div>
            } @else {
              Complete Task
            }
          </button>
          <button class="skip-button skip-desktop" (click)="onSkipTask()">
            @if (state.loading.skip) {
              <div class="oph-white-spinner">
                <mat-spinner class="custom-color" diameter="20"></mat-spinner>
              </div>
            } @else {
              Skip Task
            }
          </button>
        }
      </div>

      <div class="desktop-right-container">
        <div #contentContainer class="desktop-right-container-scroll-container oph-scroll-bar" style="overflow: hidden">
          @if (state.activeSubtask) {
            @if (state.activeSubtask?.tracker) {
              <current-sked-task-detail-metric-update-list
                [subtask]="state.activeSubtask"
                [subtaskIndex]="state.activeSubtaskIndex"
                [loading]="state.loading"
                [subtaskNotAvailable]="state.ableToCompleteSubtaskIndex < state.activeSubtaskIndex"
                [areAllMetricsReadOnly]="state.areAllMetricsReadOnly"
                [notInSked]="notInSked"
                [taskExpired]="state.taskExpired"
                (updateMetrics)="
                  onUpdateMetricValues(
                    $event.metrics,
                    $event.timestamp,
                    state.activeSubtask,
                    state.activeSubtaskIndex,
                    state.task._id,
                    false
                  )
                "
              ></current-sked-task-detail-metric-update-list>
            } @else {
              <div
                [matTooltip]="
                  state.ableToCompleteSubtaskIndex < state.activeSubtaskIndex
                    ? 'Subtasks must be completed in order'
                    : ''
                "
                [style.maxWidth]="'290px'"
              >
                <current-sked-detail-update-button
                  [loading]="state.loading"
                  [subtaskIndex]="state.activeSubtaskIndex"
                  [disabled]="state.ableToCompleteSubtaskIndex < state.activeSubtaskIndex"
                  (buttonClick)="onUpdateSubtaskStatus(true, state.activeSubtask, state.activeSubtaskIndex, false)"
                ></current-sked-detail-update-button>
              </div>
            }

            <button
              type="button"
              *userHasLens="LensSettings.ViewAndCreateNotes"
              (click)="onNotesClick()"
              class="notes-button"
            >
              <oph-icon name="notes-current-sked" [size]="13"></oph-icon>
              Notes
            </button>

            <div class="notes-drawer" [class.open]="isNotesDrawerOpen">
              <current-sked-notes
                *userHasLens="LensSettings.ViewAndCreateNotes"
                #notesComponent
                [task]="state.task"
                [subtask]="state.activeSubtask"
                [uploadImage]="uploadImage"
                (closeNotes)="onNotesClose()"
                (updateSubtask)="onPostNoteSuccess($event)"
              ></current-sked-notes>
            </div>

            <div class="current-sked-quill">
              @if (state.activeSubtask?.body) {
                <quill-view [content]="state.activeSubtask.body"></quill-view>
              } @else {
                <div class="empty">This subtask has no instructions</div>
              }
            </div>
          }
        </div>
      </div>
    </div>

    <!-- ****************************************************************************** -->
    <!-- ****************************** MOBILE VIEW ********************************** -->
    <!-- ****************************************************************************** -->

    <div class="mobile" #mobileContainer>
      <!-- --------------------------------SUBTASK LIST------------------------------------ -->
      @if (!state.mobileSubtaskDetailView) {
        <oph-back-button (buttonClick)="onBack()" class="mobile-back-button"></oph-back-button>
        <current-sked-task-detail-info
          [task]="state.task"
          [project]="state.projectsMap[state.task?.projectId]"
          [coinTypeName]="(coinTypesMap$ | async)[state.task.taskCoinType]?.name"
          [isMobile]="true"
          [showProjectAndCoin]="true"
        >
          @if (notInSked) {
            <current-sked-task-detail-status-button
              [loading]="state.loading"
              [task]="state.task"
              (statusAction)="onUpdateTaskStatus($event)"
              [availableTaskActions]="state.availableTaskActions"
            ></current-sked-task-detail-status-button>
          }
        </current-sked-task-detail-info>
        <div class="mobile-subtask-container">
          @for (subtask of state.task.subtasks; track subtask; let subtaskIndex = $index) {
            <button type="button" class="subtask mobile-subtask" (click)="onSubtaskClick(subtask, subtaskIndex, true)">
              <current-sked-checkbox
                [checked]="subtask.complete"
                [loading]="state.loading?.subtaskIndex === subtaskIndex && state.loading?.checkbox"
                [disabled]="!subtask.complete"
                (checkboxClick)="onUpdateSubtaskStatus($event, subtask, subtaskIndex, true)"
              ></current-sked-checkbox>
              <span class="subtask-title">{{ subtask.title }}</span>

              <oph-icon name="chevron-right-orange" [size]="13"></oph-icon>
            </button>
          }

          @if (state.task?.status !== TaskStatuses.Completed) {
            <button
              *userHasLens="LensSettings.CompleteTasksWithoutSubtaskCompletion"
              class="complete-task-button"
              type="button"
              (click)="onUpdateTaskStatus(TaskStatusActions.Complete)"
            >
              @if (state.loading.action) {
                <div class="oph-green-spinner">
                  <mat-spinner class="custom-color" diameter="20"></mat-spinner>
                </div>
              } @else {
                Complete Task
              }
            </button>
          }
        </div>
      } @else {
        <!-- --------------------------------SUBTASK DETAIL------------------------------------ -->
        <oph-back-button class="mobile-back-button" (buttonClick)="onMobileSubtaskDetailBack()"></oph-back-button>
        <div #mobileContentContainer class="mobile-content-container" style="overflow: hidden">
          <current-sked-task-detail-info
            [name]="state.activeSubtask?.title || 'Subtask'"
            [task]="state.task"
            [project]="state.projectsMap[state.task?.projectId]"
            [coinTypeName]="(coinTypesMap$ | async)[state.task.taskCoinType]?.name"
            [isMobile]="true"
            [showProjectAndCoin]="state.taskHasOneSubtask"
          >
            @if (notInSked && state.taskHasOneSubtask) {
              <current-sked-task-detail-status-button
                [loading]="state.loading"
                [task]="state.task"
                (statusAction)="onUpdateTaskStatus($event)"
                [availableTaskActions]="state.availableTaskActions"
              ></current-sked-task-detail-status-button>
            }
          </current-sked-task-detail-info>

          @if (state.activeSubtask?.tracker) {
            <current-sked-task-detail-metric-update-list
              [subtask]="state.activeSubtask"
              [subtaskIndex]="state.activeSubtaskIndex"
              [loading]="state.loading"
              [subtaskNotAvailable]="state.ableToCompleteSubtaskIndex < state.activeSubtaskIndex"
              (updateMetrics)="
                onUpdateMetricValues(
                  $event.metrics,
                  $event.timestamp,
                  state.activeSubtask,
                  state.activeSubtaskIndex,
                  state.task._id,
                  true
                )
              "
            ></current-sked-task-detail-metric-update-list>
          } @else {
            <current-sked-detail-update-button
              class="mobile-update-button small-screen-full-width"
              [loading]="state.loading"
              [subtaskIndex]="state.activeSubtaskIndex"
              [disabled]="state.ableToCompleteSubtaskIndex < state.activeSubtaskIndex"
              [buttonText]="state.taskHasOneSubtask ? 'Complete Task' : 'Complete Subtask'"
              (buttonClick)="onUpdateSubtaskStatus(true, state.activeSubtask, state.activeSubtaskIndex, true)"
            ></current-sked-detail-update-button>
          }

          <button
            *userHasLens="LensSettings.ViewAndCreateNotes"
            (click)="onNotesClick()"
            type="button"
            class="notes-button"
          >
            <oph-icon name="notes-current-sked" [size]="13"></oph-icon>
            Notes
          </button>

          <div class="notes-drawer" [class.open]="isNotesDrawerOpen">
            <current-sked-notes
              *userHasLens="LensSettings.ViewAndCreateNotes"
              #notesComponent
              [task]="state.task"
              [subtask]="state.activeSubtask"
              [uploadImage]="uploadImage"
              (closeNotes)="onNotesClose()"
              (updateSubtask)="onPostNoteSuccess($event)"
            ></current-sked-notes>
          </div>

          <div class="current-sked-quill mobile-quill-container">
            @if (state.activeSubtask?.body) {
              <quill-view [content]="state.activeSubtask.body"></quill-view>
            } @else {
              <div class="empty mobile-empty">This subtask has no instructions</div>
            }
          </div>
        </div>
      }
    </div>
  }
}

<oph-drawer #drawer />
